import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { BookOpen, Languages, Plus, TrendingUp, Users, Eye, Settings, Database, CreditCard } from 'lucide-react'
import { apiClient } from '../../lib/api'

interface DashboardStats {
  totalBooks: number
  totalTranslations: number
  totalUsers: number
  recentBooks: any[]
}

export function AdminDashboard() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    totalBooks: 0,
    totalTranslations: 0,
    totalUsers: 0,
    recentBooks: []
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      const data = await apiClient.getAdminStats()
      setStats(data.stats)
    } catch (error) {
      console.error('Failed to load admin stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-primary rounded-full"></div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                Manage your library books and translations
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/admin/management')} size="lg" variant="outline">
              <Database className="mr-2 h-5 w-5" />
              Manage Books
            </Button>
            <Button onClick={() => navigate('/admin/memberships')} size="lg" variant="outline">
              <CreditCard className="mr-2 h-5 w-5" />
              Memberships
            </Button>
            <Button onClick={() => navigate('/admin/books/new')} variant="default" size="lg">
              <Plus className="mr-2 h-5 w-5" />
              Add Book
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Books</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '...' : stats.totalBooks}</div>
              <p className="text-xs text-muted-foreground">
                Original books in library
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Translations</CardTitle>
              <Languages className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '...' : stats.totalTranslations}</div>
              <p className="text-xs text-muted-foreground">
                Available translations
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '...' : stats.totalUsers}</div>
              <p className="text-xs text-muted-foreground">
                Registered users
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Growth</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">+12%</div>
              <p className="text-xs text-muted-foreground">
                This month
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Books - Small Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Performance Overview</CardTitle>
                  <CardDescription>
                    System metrics and activity
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <TrendingUp className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Performance charts and metrics coming soon</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Recent Books</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/admin/management')}
                >
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : stats.recentBooks.length === 0 ? (
                <div className="text-center py-6">
                  <BookOpen className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">
                    No books yet
                  </p>
                  <Button onClick={() => navigate('/admin/books/new')} size="sm">
                    <Plus className="mr-2 h-3 w-3" />
                    Add Book
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {stats.recentBooks.slice(0, 5).map((book: any) => (
                    <div
                      key={book.id}
                      className="recent-book-link group p-2 border border-border rounded hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/books/${book.id}`)}
                    >
                      <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1">
                        {book.title_international}
                      </h4>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {book.author_international}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="quick-action-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/management')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Data Management
              </CardTitle>
              <CardDescription>
                Manage books, translations, and jobs with bulk operations
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="quick-action-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/memberships')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Memberships
              </CardTitle>
              <CardDescription>
                Manage user subscriptions and billing
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="quick-action-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/settings')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Settings
              </CardTitle>
              <CardDescription>
                Configure default LLM for auto-translation
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="quick-action-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/library')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                View Library
              </CardTitle>
              <CardDescription>
                See the library as users see it
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </div>
  )
}

